import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { AuthStore } from '../stores/authStore';
import { reaction } from 'mobx';
import { API_URL } from '../exports/fetch/GlobalFetchSettings';

const SocketContext = createContext();

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) throw new Error('useSocket must be used within a SocketProvider');
    return context;
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    
    const socketRef = useRef(null);

    useEffect(() => {
        console.log('=== SOCKET CONTEXT INIT ===');
        console.log('API_URL from GlobalFetchSettings:', API_URL);

        // Remove '/api' from the end only, not from 'https://'
        const socketUrl = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;
        console.log('Socket URL (after removing /api):', socketUrl);

        const createSocketConnection = (token) => {
            if (!token) {
                console.warn('❌ No token available - cannot create socket');
                return null;
            }

            console.log('✅ Creating socket connection to:', socketUrl);
            const newSocket = io(socketUrl, { auth: { token } });

            newSocket.on('connect', () => {
                setIsConnected(true);
            });

            newSocket.on('disconnect', () => {
                setIsConnected(false);
            });

            newSocket.on('connect_error', (error) => {
                console.error('Socket connection error:', error.message);
            });

            return newSocket;
        };

        const initialToken = AuthStore.accessToken;
        if (initialToken) {
            const newSocket = createSocketConnection(initialToken);
            if (newSocket) {
                socketRef.current = newSocket;
                setSocket(newSocket);
            }
        }
        
        const dispose = reaction(
            () => AuthStore.accessToken,
            (token) => {
                if (socketRef.current) {
                    socketRef.current.close();
                    socketRef.current = null;
                    setSocket(null);
                    setIsConnected(false);
                }

                if (token) {
                    const newSocket = createSocketConnection(token);
                    if (newSocket) {
                        socketRef.current = newSocket;
                        setSocket(newSocket);
                    }
                }
            }
        );

        return () => {
            dispose();
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, []);

    const joinRoom = (roomId) => {
        if (socketRef.current && isConnected) {
            socketRef.current.emit('join-room', roomId);
        }
    };

    const leaveRoom = (roomId) => {
        if (socketRef.current && isConnected) {
            socketRef.current.emit('leave-room', roomId);
        }
    };

    const emitRoomUpdate = (roomId, data) => {
        if (socketRef.current && isConnected) {
            socketRef.current.emit('room-update', roomId, data);
        }
    };

    return (
        <SocketContext.Provider value={{ socket, isConnected, joinRoom, leaveRoom, emitRoomUpdate }}>
            {children}
        </SocketContext.Provider>
    );
};